import { useState, useRef, useEffect } from "react";
import { useLenis } from "src/context/LenisContext";

type Direction = "left" | "right";

interface Options {
  initClipPath?: string;
  direction?: Direction;
}

export const useClipPathOnScroll = <T extends HTMLElement>(
  options: Options = { direction: "right", initClipPath: "inset(0% 0% 50% 50% round 8px)" }
) => {
  const lenis = useLenis();
  const ref = useRef<T>(null);
  const [clipPath, setClipPath] = useState(options.initClipPath);

  useEffect(() => {
    if (!lenis) return;
    const handleScrollEffect = () => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (rect.bottom <= windowHeight) return;

      if (rect.top < windowHeight && rect.bottom > 0) {
        const start = windowHeight;
        const end = Math.max(windowHeight - rect.height, 0);
        const progress = Math.min(Math.max((start - rect.top) / (start - end), 0), 1);

        const insetValue = 50 * (1 - progress);
        const radius = 8 + 2 * progress;
        if (options.direction === "right") {
          const newClip = `inset(0% 0% ${insetValue.toFixed(1)}% ${insetValue.toFixed(1)}% round ${radius.toFixed(1)}px)`;
          setClipPath(newClip);
        } else {
          const newClip = `inset(0% ${insetValue.toFixed(1)}% ${insetValue.toFixed(1)}% 0% round ${radius.toFixed(1)}px)`;
          setClipPath(newClip);
        }
      }
    };

    lenis.on("scroll", handleScrollEffect);
    return () => {
      lenis.off("scroll", handleScrollEffect);
    };
  }, [lenis, options.direction]);

  return { ref, clipPath };
};
